import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from '@usas/ui-web/wrappers/ReactRouterDom';

import useOmrData from '../../state/omr/UseOmrData';
import usePersonNcsaOptInData from '../../state/personNcsaOptIn/UsePersonNcsaOptInData';

import NavLinks from '../../NavLinks';

import ToIntIfInt from '@usas/ui-web/utils/ToIntIfInt';
import UseForm from '@usas/ui-web/utils/UseForm';
import Constants from '@usas/ui-web/utils/Constants';

const useNcsaOptIn = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { personState, savePersonState, hasAthleteOffering } = useOmrData();
  const { personNcsaOptInState, postPersonNcsaOptIn } = usePersonNcsaOptInData();
  const [state1, setState] = useState({
    ncsaOptInErrorMessage: '',
    highSchoolGraduationYearErrorMessage: '',
    showModal: false
  });

  const normalizeGraduationYear = (value) => {
    if (value == null) {
      return null;
    }

    const trimmedValue = value.toString().trim();
    if (trimmedValue === '' || trimmedValue === '-1') {
      return null;
    }

    const parsedValue = ToIntIfInt(trimmedValue);
    return typeof parsedValue === 'number' ? parsedValue : null;
  };

  const isValidGraduationYear = (value) => normalizeGraduationYear(value) !== null;

  const getGenderPath = () => {
    if (personState?.is16orOlder === true) {
      return hasAthleteOffering(personState)
        ? NavLinks.ATHLETE_COMP_GENDER_16_AND_UP
        : NavLinks.GENDER_IDENTITY_16_AND_UP;
    }

    return NavLinks.ATHLETE_COMP_GENDER_U16;
  };

  const onItemClick = (value) => {
    const currentNcsaOptIn = personState?.person?.personNcsaOptIn || {};
    const newObj = {
      ...currentNcsaOptIn,
      ncsaOptIn: value,
      highSchoolGraduationYear: value === false
        ? null
        : normalizeGraduationYear(currentNcsaOptIn.highSchoolGraduationYear)
    };

    const hasChanged =
      newObj.ncsaOptIn !== currentNcsaOptIn.ncsaOptIn
      || newObj.highSchoolGraduationYear !== currentNcsaOptIn.highSchoolGraduationYear;

    if (hasChanged === true) {
      const newPersonState = JSON.parse(JSON.stringify(personState));
      newPersonState.person.personNcsaOptIn = JSON.parse(JSON.stringify(newObj));
      savePersonState(newPersonState, false);

      if (state1.ncsaOptInErrorMessage !== '' || (value === false && state1.highSchoolGraduationYearErrorMessage !== '')) {
        setState({ ...state1, ncsaOptInErrorMessage: '', highSchoolGraduationYearErrorMessage: '' });
      }
    }
  };

  const onHighSchoolGraduationYearChanged = (value) => {
    const currentNcsaOptIn = personState?.person?.personNcsaOptIn || {};
    const normalizedGraduationYear = normalizeGraduationYear(value);
    const currentNormalizedGraduationYear = normalizeGraduationYear(currentNcsaOptIn.highSchoolGraduationYear);

    if (normalizedGraduationYear === currentNormalizedGraduationYear) {
      return;
    }

    const newPersonState = JSON.parse(JSON.stringify(personState));
    newPersonState.person.personNcsaOptIn = {
      ...newPersonState.person.personNcsaOptIn,
      highSchoolGraduationYear: normalizedGraduationYear
    };
    savePersonState(newPersonState, false);

    if (state1.highSchoolGraduationYearErrorMessage !== '' && isValidGraduationYear(normalizedGraduationYear)) {
      setState({ ...state1, highSchoolGraduationYearErrorMessage: '' });
    }
  };

  const onNextButtonClicked = (e) => {
    e?.preventDefault();
    document.getElementById('omrHeadingId')?.scrollIntoView();

    const selectedNcsaOptIn = personState.person.personNcsaOptIn?.ncsaOptIn;

    if (selectedNcsaOptIn !== true && selectedNcsaOptIn !== false) {
      setState({
        ...state1,
        showModal: false,
        ncsaOptInErrorMessage: "Please select 'Yes' or 'No'",
        highSchoolGraduationYearErrorMessage: ''
      });
      return;
    }

    if (selectedNcsaOptIn === true
      && !isValidGraduationYear(personState.person.personNcsaOptIn?.highSchoolGraduationYear)) {
      setState({
        ...state1,
        showModal: false,
        ncsaOptInErrorMessage: '',
        highSchoolGraduationYearErrorMessage: 'High School Graduation Year is required'
      });
      return;
    }

    if (selectedNcsaOptIn === true) {
      setState({ ...state1, showModal: true, ncsaOptInErrorMessage: '', highSchoolGraduationYearErrorMessage: '' });
    } else {
      navigate(NavLinks.REGISTRATION_ACKNOWLEDGEMENTS);
    }
  };

  const onConfirm = (e) => {
    e?.preventDefault();
    navigate(NavLinks.REGISTRATION_ACKNOWLEDGEMENTS);
  };

  const onModalCanceled = (e) => {
    e?.preventDefault();

    setState({ ...state1, showModal: false });
  };

  const onBackButtonClicked = (e) => {
    e?.preventDefault();
    document.getElementById('omrHeadingId')?.scrollIntoView();
    navigate(getGenderPath());
  };

  useEffect(() => {
    if (personState?.person) {
      if (personNcsaOptInState.isObjLoaded !== true) {
        const personId = personState?.person?.personId !== '' ? personState.person.personId : null;
        const birthDate = personState?.person?.birthDate !== Constants.BLANK_DATE_STRING ? personState.person.birthDate : null;
        const postObj = {
          personId: personId,
          birthDate: birthDate,
          isForLoggedInUser: personState?.isMembershipForLoggedInUser,
          offeringInstanceIds: personState?.person.offeringInstanceIds
        }
        postPersonNcsaOptIn(postObj);
      } else if (personNcsaOptInState.isObjLoaded === true) {
        if (personNcsaOptInState.objData.showNcsaOptIn === false) {
          const cameFromGenderPage = location?.state?.fromGenderPage === true;
          const cameFromAcknowledgementsPage = location?.state?.fromAcknowledgementsPage === true;

          if (cameFromGenderPage) {
            navigate(NavLinks.REGISTRATION_ACKNOWLEDGEMENTS);
          } else {
            navigate(cameFromAcknowledgementsPage
              ? getGenderPath()
              : NavLinks.REGISTRATION_ACKNOWLEDGEMENTS);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personState, personNcsaOptInState]);

  return {
    ncsaOptIn: personState.person.personNcsaOptIn.ncsaOptIn,
    highSchoolGraduationYear: normalizeGraduationYear(personState.person.personNcsaOptIn.highSchoolGraduationYear),
    showNcsaOptIn: personNcsaOptInState.objData?.showNcsaOptIn ?? false,
    imageUrl: personNcsaOptInState.objData?.ncsaLogoUrl || '',
    isLoading: personNcsaOptInState.isObjLoading,
    ncsaOptInErrorMessage: state1.ncsaOptInErrorMessage,
    highSchoolGraduationYearErrorMessage: state1.highSchoolGraduationYearErrorMessage,
    showModal: state1.showModal,
    onNextButtonClicked,
    onBackButtonClicked,
    onItemClick,
    onHighSchoolGraduationYearChanged,
    onModalCanceled,
    onConfirm
  };
};

export default useNcsaOptIn;
